import { FooterMenu, Loading } from "components"
import { AttachmentModal, ExportExcelAdjustmentInventory, FormSection, JournalAdjusment, PrintAdjustmentInventory } from "./components"
import { DetailDataContext } from "./contexts"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import { DataCountType } from "../types"
import { useApi, useToggle } from "utils"
import { getValuesByDifferent, useDataCountDetail } from "../utils"
import { type FormType, validationSchema, getAccValues } from "./utils"

export default function CreateAdjustmentInventory(): JSX.Element {
  const { count_id } = useParams()
  const { data, isLoading } = useDataCountDetail(count_id!)

  if (isLoading || !data) {
    return <Loading errorText="No data available" loading={isLoading} />
  }

  return <ViewSection data={data} />
}

function ViewSection(params: { data: DataCountType }): JSX.Element {
  const { isActive, toggle } = useToggle(false)
  const api = useApi()
  const navigate = useNavigate()

  const dataCount = params.data
  const invCount = params.data.invcount

  const defaultValues: FormType = {
    memo: "",
    employee_id: invCount.employee_id.toString() || "",
    transaction_date: moment().format("YYYY-MM-DD"),
    trx_code: "",
    adjusmentDetail: dataCount.countdetail,
    attach_file: []
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })

  const transaction_date = useWatch({ control: methods?.control, name: "transaction_date" })
  
  const onSubmit = (value: FormType) => {
    const finalValue = {
      ...value,
      count_id: invCount.count_id,
      location_id: invCount.location_id,
      adjustmentdetail: dataCount.countdetail.map((item) => {
        const acc = getAccValues(item)
        const differentValue = getValuesByDifferent(item.different)
        const formItem = value.adjusmentDetail?.find(fd => fd.count_detail_id === item.count_detail_id)

        return {
          product_id: item.product_id,
          mr_unit_id: item.mr_unit_id,
          different_adjustment: item.different,
          // different_unit: item.different * item.quantity_unit_sell_convention,
          different_unit: item.different,
          avg_price_use: item.avg_price_use,
          amount_adjustment: acc.amount_adjustment,
          position: differentValue.position === "credit" ? 2 : 1,
          coa_inventory_id: formItem?.coa_inventory_id ?? item.coa_inventory_id,
          coa_coex_id: formItem?.coa_coex_id ?? item.coa_coex_id,
        }
      }),
      attach_file: value?.attach_file?.map(file => ({
        ...file,
        adjustment_id: invCount.count_id,
      })),
    }
    return new Promise<void>((resolve) => {
      toast
        .promise(api.post("/invadjustment/add", finalValue), {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message
        })
        .then(() => {
          navigate("/inventory/transaction/adjustment-inventory")
        })
        .catch(() => {})
        .finally(resolve)
    })
  }
    

  return (
    <DetailDataContext.Provider value={params.data}>
      <FormProvider {...methods}>
        <FormSection
          footerElement={
            <FooterMenu
              hasCancelButton
              export={{ actiontype: "export", onClick: () => ExportExcelAdjustmentInventory({ countdetail: dataCount.countdetail, transaction_date }) }}
              customElement={{ 
                journal: <JournalAdjusment type="count" />, 
                attachment: <AttachmentModal /> 
              }}
              print={{ actiontype: "print", onClick: toggle }}
              save={{
                actiontype: "save",
                loading: methods.formState.isSubmitting ? "true" : undefined,
                onClick: methods.handleSubmit(onSubmit)
              }}
            />
          }
        />

        {isActive ? (<PrintAdjustmentInventory toggle={toggle} />) : null}
      </FormProvider>
    </DetailDataContext.Provider>
  )
}
